import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiArrowLeft,
  FiArrowRight,
  FiClock,
  FiTarget,
  FiBookOpen,
  FiCheck,
  FiX,
  FiZap,
  FiBarChart,
  FiHome,
} from 'react-icons/fi';
import '../styles/LessonStyles.css';
import GetStartedWith from '../../../../components/GetStartedWith';

const timeAllocationStrategies = [
  {
    id: 1,
    scenario: 'Complex Single Passage (8 minutes, 3 questions)',
    description: 'Dense academic passage with multiple arguments',
    timeBreakdown: {
      reading: '4-5 minutes',
      perQuestion: '30 seconds',
      review: '1 minute',
    },
    strategy: 'Deep comprehension first, then rapid question answering',
    benefits: [
      'Complete understanding prevents re-reading',
      'Questions become much easier to answer',
      'Higher accuracy rate',
      'Confidence in answers',
    ],
    example:
      'A 400-word philosophy or science passage with intricate arguments',
  },
  {
    id: 2,
    scenario: 'Multiple Short Passages (10 minutes, 6 questions)',
    description: 'Two shorter passages with 3 questions each',
    timeBreakdown: {
      reading: '2 minutes per passage',
      perQuestion: '1 minute',
      review: '2 minutes total',
    },
    strategy: 'Balanced approach - moderate depth, steady pace',
    benefits: [
      'Consistent rhythm across passages',
      'Adequate time for each passage type',
      'Flexibility to adjust if needed',
    ],
    example: 'Two 200-word passages on different topics',
  },
  {
    id: 3,
    scenario: 'Long Multi-Question Passage (12 minutes, 5 questions)',
    description: 'Extensive passage with diverse question types',
    timeBreakdown: {
      reading: '5-6 minutes',
      perQuestion: '1 minute',
      review: '1-2 minutes',
    },
    strategy: 'Thorough initial reading with structured approach',
    benefits: [
      'Handles complexity effectively',
      'Supports multiple question types',
      'Reduces cognitive load during questions',
    ],
    example: 'A 500+ word historical or literary analysis passage',
  },
];

const masteringTechniques = [
  {
    id: 1,
    technique: 'Active Structural Reading',
    description: 'Map the passage structure as you read',
    steps: [
      'Identify the main thesis in paragraph 1',
      'Note the role of each subsequent paragraph',
      'Track transitions and logical flow',
      'Recognize conclusion and implications',
    ],
    timeTarget: '30 seconds per paragraph for structure mapping',
  },
  {
    id: 2,
    technique: 'Author Intent Focus',
    description: 'Understand what the author is trying to accomplish',
    steps: [
      'Ask: "What is the author trying to prove?"',
      'Identify the target audience',
      "Note the author's tone and attitude",
      'Understand the purpose of each example',
    ],
    timeTarget: '1 minute total for intent analysis',
  },
  {
    id: 3,
    technique: 'Evidence Hierarchy',
    description: 'Distinguish between main points and supporting details',
    steps: [
      'Identify primary arguments vs. examples',
      'Note which evidence supports which claims',
      'Understand cause-effect relationships',
      'Distinguish facts from opinions',
    ],
    timeTarget: '45 seconds for evidence mapping',
  },
  {
    id: 4,
    technique: 'Question Anticipation',
    description: 'Predict likely questions while reading',
    steps: [
      'Think: "What would they ask about this?"',
      'Note potentially confusing sections',
      'Identify areas with multiple interpretations',
      'Mark key terms and definitions',
    ],
    timeTarget: '30 seconds for question prediction',
  },
];

const practiceScenarios = [
  {
    id: 1,
    title: 'Complex Science Passage',
    timeLimit: '8 minutes total (5 min reading + 3 questions)',
    passage: `Recent advances in quantum computing have brought this revolutionary technology closer to practical applications, though significant challenges remain. Unlike classical computers that process information using bits representing either 0 or 1, quantum computers utilize quantum bits or "qubits" that can exist in multiple states simultaneously through a phenomenon called superposition.

The power of quantum computing lies in its ability to perform certain calculations exponentially faster than classical computers. For problems involving large datasets and complex algorithms, such as cryptography, drug discovery, and financial modeling, quantum computers could provide solutions in minutes that would take classical computers thousands of years.

However, quantum computers are extremely sensitive to environmental interference. The delicate quantum states required for computation can be disrupted by temperature fluctuations, electromagnetic fields, and even cosmic radiation. This fragility necessitates sophisticated error correction mechanisms and carefully controlled operating environments, typically requiring temperatures near absolute zero.

Current quantum computers remain largely experimental, with most capable of maintaining quantum coherence for only brief periods. Companies like IBM, Google, and startups worldwide are racing to develop more stable systems with greater numbers of qubits. The quantum computing industry represents a fundamental shift in computational paradigms, potentially revolutionizing fields from artificial intelligence to materials science.`,
    questions: [
      {
        id: 1,
        question:
          'What is the primary challenge facing quantum computing according to the passage?',
        options: [
          'The high cost of development',
          'Environmental sensitivity disrupting quantum states',
          'Lack of practical applications',
          'Competition between companies',
        ],
        correct: 1,
        reasoning:
          'The passage emphasizes that quantum computers are "extremely sensitive to environmental interference" and this "fragility necessitates sophisticated error correction mechanisms."',
      },
      {
        id: 2,
        question:
          "The author's attitude toward quantum computing can best be described as:",
        options: [
          'Skeptical about its future potential',
          'Optimistic but realistic about current limitations',
          'Enthusiastic without acknowledging problems',
          'Neutral and purely informational',
        ],
        correct: 1,
        reasoning:
          'The author acknowledges both the "revolutionary" potential and "significant challenges," showing balanced optimism with realistic assessment.',
      },
      {
        id: 3,
        question:
          'According to the passage, quantum computers differ from classical computers primarily in:',
        options: [
          'Their processing speed for all types of calculations',
          'The use of qubits that can exist in multiple states simultaneously',
          'Their ability to operate at room temperature',
          'Their lower cost of operation',
        ],
        correct: 1,
        reasoning:
          'The passage explicitly states that quantum computers "utilize quantum bits or \'qubits\' that can exist in multiple states simultaneously through a phenomenon called superposition."',
      },
    ],
  },
];

export default function MasterPassageLesson() {
  const navigate = useNavigate();
  const practiceRef = useRef(null);
  const [selectedStrategy, setSelectedStrategy] = useState(null);
  const [selectedTechnique, setSelectedTechnique] = useState(null);
  const [practiceStartTime, setPracticeStartTime] = useState(null);
  const [practiceAnswers, setPracticeAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [readingTime, setReadingTime] = useState(null);

  const scrollToPractice = () => {
    practiceRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const startPractice = () => {
    setPracticeStartTime(Date.now());
    setReadingTime(null);
    setPracticeAnswers({});
    setShowResults(false);
  };

  const finishReading = () => {
    if (practiceStartTime) {
      setReadingTime(Date.now() - practiceStartTime);
    }
  };

  const handleAnswerSelect = (questionId, optionIndex) => {
    setPracticeAnswers((prev) => ({
      ...prev,
      [questionId]: optionIndex,
    }));
  };

  const calculateResults = () => {
    const scenario = practiceScenarios[0];
    const correct = scenario.questions.filter(
      (q) => practiceAnswers[q.id] === q.correct
    ).length;
    const total = scenario.questions.length;
    const readingTimeMin = readingTime ? (readingTime / 60000).toFixed(1) : 0;

    return {
      correct,
      total,
      percentage: Math.round((correct / total) * 100),
      readingTimeMin,
      readingTimeTarget:
        readingTimeMin <= 5
          ? 'Excellent'
          : readingTimeMin <= 6
            ? 'Good'
            : 'Too slow',
    };
  };

  const renderOverview = () => (
    <div className="lesson-content">
      <h2 className="section-title">Master the Passage First Strategy</h2>

      <div className="lesson-intro">
        <p>
          For complex passages with few questions, invest more time in deep
          comprehension upfront. This counterintuitive approach leads to higher
          accuracy and confidence. When you truly understand the passage,
          questions become much easier to answer quickly.
        </p>
      </div>

      {/* Time Allocation Strategies */}
      <div style={{ marginBottom: '3rem' }}>
        <h3 style={{ color: 'var(--liwaza-on-surface)', marginBottom: '1rem' }}>
          Time Allocation by Scenario
        </h3>
        <div className="question-types-grid">
          {timeAllocationStrategies.map((strategy) => (
            <div
              key={strategy.id}
              onClick={() => setSelectedStrategy(strategy)}
              className={`question-type-card ${selectedStrategy?.id === strategy.id ? 'selected' : ''}`}
            >
              <div className="type-header">
                <div className="type-number">
                  <FiClock />
                </div>
                <h4 className="type-title">{strategy.scenario}</h4>
              </div>
              <p className="type-description">{strategy.description}</p>
              <div
                style={{
                  background: 'var(--liwaza-primary-container)',
                  color: 'var(--liwaza-on-primary-container)',
                  padding: '0.5rem',
                  borderRadius: '6px',
                  marginTop: '1rem',
                  fontSize: '0.8rem',
                }}
              >
                Reading: {strategy.timeBreakdown.reading} | Questions:{' '}
                {strategy.timeBreakdown.perQuestion}
              </div>
              <button
                onClick={() => setSelectedStrategy(strategy)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--liwaza-primary)',
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                  padding: '0.5rem 0',
                  marginTop: '1rem',
                  textAlign: 'left',
                  width: '100%',
                  fontWeight: 500,
                }}
              >
                Learn more about →
              </button>
            </div>
          ))}
        </div>
      </div>

      {selectedStrategy && (
        <div className="detailed-analysis">
          <h3 className="analysis-title">
            <FiTarget />
            {selectedStrategy.scenario}
          </h3>

          <div className="analysis-section">
            <h4>Time Breakdown:</h4>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: '1rem',
                marginBottom: '1rem',
              }}
            >
              <div
                style={{
                  background: 'var(--liwaza-surface-container)',
                  padding: '1rem',
                  borderRadius: '8px',
                  textAlign: 'center',
                }}
              >
                <div
                  style={{
                    color: 'var(--liwaza-primary)',
                    fontWeight: 'bold',
                    fontSize: '1.2rem',
                  }}
                >
                  {selectedStrategy.timeBreakdown.reading}
                </div>
                <div
                  style={{
                    color: 'var(--liwaza-on-surface-variant)',
                    fontSize: '0.9rem',
                  }}
                >
                  Initial Reading
                </div>
              </div>
              <div
                style={{
                  background: 'var(--liwaza-surface-container)',
                  padding: '1rem',
                  borderRadius: '8px',
                  textAlign: 'center',
                }}
              >
                <div
                  style={{
                    color: 'var(--liwaza-primary)',
                    fontWeight: 'bold',
                    fontSize: '1.2rem',
                  }}
                >
                  {selectedStrategy.timeBreakdown.perQuestion}
                </div>
                <div
                  style={{
                    color: 'var(--liwaza-on-surface-variant)',
                    fontSize: '0.9rem',
                  }}
                >
                  Per Question
                </div>
              </div>
              <div
                style={{
                  background: 'var(--liwaza-surface-container)',
                  padding: '1rem',
                  borderRadius: '8px',
                  textAlign: 'center',
                }}
              >
                <div
                  style={{
                    color: 'var(--liwaza-primary)',
                    fontWeight: 'bold',
                    fontSize: '1.2rem',
                  }}
                >
                  {selectedStrategy.timeBreakdown.review}
                </div>
                <div
                  style={{
                    color: 'var(--liwaza-on-surface-variant)',
                    fontSize: '0.9rem',
                  }}
                >
                  Review Time
                </div>
              </div>
            </div>
          </div>

          <div className="analysis-section">
            <h4>Strategy:</h4>
            <div className="strategy-box">{selectedStrategy.strategy}</div>
          </div>

          <div className="analysis-section">
            <h4 className="tips-header">Benefits:</h4>
            <ul className="tips-list">
              {selectedStrategy.benefits.map((benefit, index) => (
                <li key={index}>{benefit}</li>
              ))}
            </ul>
          </div>

          <div className="analysis-section">
            <h4>Example:</h4>
            <div
              style={{
                background: 'var(--liwaza-surface-container-highest)',
                padding: '1rem',
                borderRadius: '8px',
                fontStyle: 'italic',
                color: 'var(--liwaza-on-surface-variant)',
              }}
            >
              {selectedStrategy.example}
            </div>
          </div>
        </div>
      )}

      {/* Mastering Techniques */}
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={{ color: 'var(--liwaza-on-surface)', marginBottom: '1rem' }}>
          Deep Reading Techniques
        </h3>
        <div className="question-types-grid">
          {masteringTechniques.map((technique) => (
            <div
              key={technique.id}
              onClick={() => setSelectedTechnique(technique)}
              className={`question-type-card ${selectedTechnique?.id === technique.id ? 'selected' : ''}`}
            >
              <div className="type-header">
                <div className="type-number">
                  <FiZap />
                </div>
                <h4 className="type-title">{technique.technique}</h4>
              </div>
              <p className="type-description">{technique.description}</p>
              <div
                style={{
                  color: 'var(--liwaza-secondary)',
                  fontSize: '0.8rem',
                  marginTop: '0.5rem',
                }}
              >
                Target: {technique.timeTarget}
              </div>
            </div>
          ))}
        </div>
      </div>

      {selectedTechnique && (
        <div className="detailed-analysis">
          <h3 className="analysis-title">
            <FiBookOpen />
            {selectedTechnique.technique}
          </h3>

          <div className="analysis-section">
            <h4>Step-by-Step Process:</h4>
            <ol
              style={{
                color: 'var(--liwaza-on-surface-variant)',
                paddingLeft: '1rem',
                lineHeight: 1.6,
              }}
            >
              {selectedTechnique.steps.map((step, index) => (
                <li key={index} style={{ marginBottom: '0.5rem' }}>
                  {step}
                </li>
              ))}
            </ol>
          </div>

          <div
            style={{
              background: 'var(--liwaza-secondary-container)',
              color: 'var(--liwaza-on-secondary-container)',
              padding: '1rem',
              borderRadius: '8px',
            }}
          >
            <strong>Time Target:</strong> {selectedTechnique.timeTarget}
          </div>
        </div>
      )}
    </div>
  );

  const renderPractice = () => (
    <div ref={practiceRef} className="practice-section">
      <h2 className="section-title">Practice: Master the Passage First</h2>

      <div className="practice-passage">
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '2rem',
          }}
        >
          <h3 className="passage-title">Timed Practice Exercise</h3>

          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            {readingTime && (
              <div
                style={{
                  background: 'var(--liwaza-primary-container)',
                  color: 'var(--liwaza-on-primary-container)',
                  padding: '0.5rem 1rem',
                  borderRadius: '8px',
                  fontSize: '0.9rem',
                }}
              >
                Reading Time: {(readingTime / 60000).toFixed(1)} minutes
              </div>
            )}

            {!practiceStartTime ? (
              <button
                onClick={startPractice}
                style={{
                  background: 'var(--liwaza-primary)',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
              >
                <FiClock /> Start Practice
              </button>
            ) : !readingTime ? (
              <button
                onClick={finishReading}
                style={{
                  background: 'var(--liwaza-secondary)',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
              >
                <FiCheck /> Finished Reading
              </button>
            ) : (
              <button
                onClick={() => setShowResults(true)}
                style={{
                  background: 'var(--liwaza-success)',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
              >
                <FiBarChart /> Show Results
              </button>
            )}
          </div>
        </div>

        <div className="passage-text">
          {practiceScenarios[0].passage
            .split('\n\n')
            .map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
        </div>

        {readingTime && (
          <div style={{ marginTop: '2rem' }}>
            <h4
              style={{
                color: 'var(--liwaza-on-surface)',
                marginBottom: '1rem',
              }}
            >
              Now answer the questions (Target: 30 seconds each)
            </h4>

            {practiceScenarios[0].questions.map((question) => (
              <div key={question.id} style={{ marginBottom: '2rem' }}>
                <p
                  style={{
                    color: 'var(--liwaza-on-surface)',
                    fontWeight: 'bold',
                    marginBottom: '1rem',
                  }}
                >
                  Question {question.id}: {question.question}
                </p>

                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.5rem',
                  }}
                >
                  {question.options.map((option, index) => (
                    <button
                      key={index}
                      onClick={() => handleAnswerSelect(question.id, index)}
                      style={{
                        background:
                          practiceAnswers[question.id] === index
                            ? 'var(--liwaza-primary-container)'
                            : 'var(--liwaza-surface-container-highest)',
                        color:
                          practiceAnswers[question.id] === index
                            ? 'var(--liwaza-on-primary-container)'
                            : 'var(--liwaza-on-surface)',
                        border:
                          practiceAnswers[question.id] === index
                            ? '2px solid var(--liwaza-primary)'
                            : '1px solid var(--liwaza-outline-variant)',
                        padding: '0.75rem',
                        borderRadius: '8px',
                        textAlign: 'left',
                        cursor: 'pointer',
                      }}
                    >
                      {String.fromCharCode(65 + index)}. {option}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {showResults && (
          <div
            style={{
              background: 'var(--liwaza-primary-container)',
              padding: '2rem',
              borderRadius: '12px',
              marginTop: '2rem',
            }}
          >
            <h4
              style={{
                color: 'var(--liwaza-on-primary-container)',
                marginBottom: '1rem',
              }}
            >
              Practice Results
            </h4>

            {(() => {
              const results = calculateResults();
              return (
                <div>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns:
                        'repeat(auto-fit, minmax(150px, 1fr))',
                      gap: '1rem',
                      marginBottom: '1rem',
                    }}
                  >
                    <div style={{ textAlign: 'center' }}>
                      <div
                        style={{
                          fontSize: '1.5rem',
                          fontWeight: 'bold',
                          color:
                            results.percentage >= 70
                              ? 'var(--liwaza-success)'
                              : 'var(--liwaza-error)',
                        }}
                      >
                        {results.correct}/{results.total}
                      </div>
                      <div
                        style={{
                          color: 'var(--liwaza-on-primary-container)',
                          fontSize: '0.9rem',
                        }}
                      >
                        Correct Answers ({results.percentage}%)
                      </div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div
                        style={{
                          fontSize: '1.5rem',
                          fontWeight: 'bold',
                          color:
                            results.readingTimeTarget === 'Excellent'
                              ? 'var(--liwaza-success)'
                              : results.readingTimeTarget === 'Good'
                                ? 'var(--liwaza-warning)'
                                : 'var(--liwaza-error)',
                        }}
                      >
                        {results.readingTimeMin}m
                      </div>
                      <div
                        style={{
                          color: 'var(--liwaza-on-primary-container)',
                          fontSize: '0.9rem',
                        }}
                      >
                        Reading Time ({results.readingTimeTarget})
                      </div>
                    </div>
                  </div>

                  <div
                    style={{
                      background: 'var(--liwaza-surface-container)',
                      padding: '1rem',
                      borderRadius: '8px',
                      color: 'var(--liwaza-on-surface)',
                    }}
                  >
                    <h5 style={{ marginBottom: '0.5rem' }}>
                      Strategy Analysis:
                    </h5>
                    {results.readingTimeMin <= 5 ? (
                      <p>
                        ✅ Excellent! You mastered the passage efficiently and
                        should have answered questions confidently.
                      </p>
                    ) : results.readingTimeMin <= 6 ? (
                      <p>
                        👍 Good timing. You invested adequate time in
                        comprehension.
                      </p>
                    ) : (
                      <p>
                        ⚠️ Reading took longer than ideal. Practice identifying
                        key information faster.
                      </p>
                    )}

                    {results.percentage >= 70 ? (
                      <p>
                        🎯 Strong performance! The "Master the Passage First"
                        strategy is working for you.
                      </p>
                    ) : (
                      <p>
                        📚 Focus on deeper structural understanding during your
                        initial reading phase.
                      </p>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="lesson-container">
      {/* Header */}
      <div className="lesson-header">
        <button
          onClick={() =>
            navigate('/exams/gre/', { state: { activeCategory: 'reading' } })
          }
          className="back-button"
        >
          <FiArrowLeft /> Back to Reading Comprehension
        </button>

        <div className="header-actions">
          <button onClick={scrollToPractice} className="practice-button">
            Practice
          </button>
        </div>
      </div>

      {/* Title */}
      <div className="lesson-title-section">
        <h1 className="lesson-title">
          <FiTarget />
          Lesson 6: Master the Passage First
        </h1>
        <p className="lesson-subtitle">
          Learn the counterintuitive strategy of investing more time in deep
          comprehension upfront. For complex passages with few questions, this
          approach leads to higher accuracy and confidence.
        </p>
      </div>

      {/* Content */}
      {renderOverview()}
      {renderPractice()}

      {/* Get Started With Component */}
      <div style={{ margin: '3rem 0 2rem 0' }}>
        <GetStartedWith
          title="Need help with the 'Master the Passage First' strategy?"
          spaceName="Verbal vault"
          humanCoachTitle="Talk to a GRE coach"
          liwazaAITitle="Ask AI"
          communityTitle="Ask other students"
          showHumanCoach={true}
          showLiwazaAI={true}
          showCommunity={true}
          aifirstmessage="I need help understanding the 'Master the Passage First' strategy for GRE Reading Comprehension"
          aicontext="For complex passages with few questions, invest more time in deep comprehension upfront. This counterintuitive approach leads to higher accuracy and confidence. When you truly understand the passage, questions become much easier to answer quickly."
        />
      </div>

      {/* Navigation */}
      <div className="lesson-navigation">
        <button
          onClick={() =>
            navigate('/exams/gre/reading-comprehension/lesson/speed-reading')
          }
          className="prev-lesson-button"
        >
          <FiArrowLeft /> Previous: Target 500 WPM
        </button>
        <button
          onClick={() =>
            navigate('/exams/gre/reading-comprehension/lesson/trap-answers')
          }
          className="next-lesson-button"
        >
          Next: Trap Answers <FiArrowRight />
        </button>
      </div>
    </div>
  );
}
